import React, { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { paperTheme, liveTheme } from './themes';
import Dashboard from './Dashboard';
import Login from './Login';
import LandingPage from './pages/LandingPage';
import Sidebar from './components/Sidebar';
import Navbar from './components/Navbar';
import FeedbackButton from './components/FeedbackButton';
import PublicFeedbackButton from './components/PublicFeedbackButton';

import GuidedTour from './components/GuidedTour';
import TokenWarning from './components/TokenWarning';
import Footer from './components/Footer';
import RiskDisclosureBanner from './components/RiskDisclosureBanner';
import DisclosureBanner from './components/DisclosureBanner';
import JurisdictionWarningModal from './components/JurisdictionWarningModal';
import CircuitBreakerAlertModal from './components/CircuitBreakerAlertModal';
import ReverificationBanner from './components/ReverificationBanner';
import { ReadOnlyProvider } from './ReadOnlyContext';
import { StrategyFilterProvider } from './StrategyFilterContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SidebarProvider } from './contexts/SidebarContext';
import { OnboardingProvider } from './contexts/OnboardingContext';
import { ManualTradingProvider } from './contexts/ManualTradingContext';
import { TradingModeProvider, useTradingMode } from './contexts/TradingModeContext';
import { TourProvider } from './contexts/TourContext';
import { ErrorNotificationProvider } from './contexts/ErrorNotificationContext';
import { ToastProvider } from './contexts/ToastContext';
import { initErrorReporter } from './services/errorReporter';
import TourRunner from './components/TourRunner';
import DisclosureGate from './components/DisclosureGate';
import api from './services/api';
import UITLAdminWrapper from './components/UITLAdminWrapper';

// Install global error handlers (window.onerror, unhandledrejection)
initErrorReporter();

// Lazy-loaded pages (loaded on demand)
const Strategies = lazy(() => import('./Strategies_Enhanced'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const AutonomousMode = lazy(() => import('./AutonomousMode'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const StrategyCreate = lazy(() => import('./pages/StrategyCreate'));
const BacktestDashboard = lazy(() => import('./pages/BacktestDashboard'));
const PortfolioOptimizer = lazy(() => import('./pages/PortfolioOptimizer'));
const StrategyDetail = lazy(() => import('./pages/StrategyDetail'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const RiskDisclosure = lazy(() => import('./pages/RiskDisclosure'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminTriagePage = lazy(() => import('./pages/AdminTriagePage'));
const AdminUsageAnalytics = lazy(() => import('./pages/AdminUsageAnalytics'));
const MarketplaceFeed = lazy(() => import('./pages/MarketplaceFeed'));
const MarketplaceListingDetail = lazy(() => import('./pages/MarketplaceListingDetail'));
const MarketplaceCreateListing = lazy(() => import('./pages/MarketplaceCreateListing'));
const MarketplaceMyListings = lazy(() => import('./pages/MarketplaceMyListings'));
const MarketplaceEarnings = lazy(() => import('./pages/MarketplaceEarnings'));
const MarketplaceSubscriptions = lazy(() => import('./pages/MarketplaceSubscriptions'));
const CreatorProfile = lazy(() => import('./pages/CreatorProfile'));
const MarketplaceHelp = lazy(() => import('./pages/MarketplaceHelp'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const MonteCarloMethodology = lazy(() => import('./pages/MonteCarloMethodology'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const CompliancePage = lazy(() => import('./pages/CompliancePage'));
const OAuthCallback = lazy(() => import('./pages/OAuthCallback'));
const AdminComplianceDashboard = lazy(() => import('./pages/AdminComplianceDashboard'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const EmailVerificationGate = lazy(() => import('./pages/EmailVerificationGate'));
const TradingIdeas = lazy(() => import('./pages/TradingIdeas'));
const ResearchFunnel = lazy(() => import('./pages/ResearchFunnel'));
const ExperimentDetail = lazy(() => import('./pages/ExperimentDetail'));

// Theme is now dynamic — see themes.ts
// paperTheme = warm teal (default), liveTheme = royal blue/navy

// Loading spinner shown while auth state is being checked
const LoadingScreen: React.FC = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: '#FDF6EC',
    }}
  >
    <CircularProgress size={48} />
  </Box>
);

// Auth guard — shows LandingPage at "/" when logged out, redirects other paths to /login
// Shows email verification gate if user hasn't verified their email
// Shows disclosure gate if user has unacknowledged active disclosures
const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // null = loading, true = pending, false = all clear
  const [hasPending, setHasPending] = useState<boolean | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !user || user.email_verified === false) {
      setHasPending(null);
      return;
    }
    api.get('/api/v1/disclosures/active')
      .then(res => {
        setHasPending(res.data.some((d: any) => d.needs_acknowledgment));
      })
      .catch(() => setHasPending(false)); // fail open — never block on API error
  }, [isAuthenticated, user?.email_verified]);

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    // Show landing page for root path instead of redirecting to login
    if (location.pathname === '/') {
      return <LandingPage />;
    }
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Gate unverified users (email check takes priority over disclosures)
  if (user && user.email_verified === false) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <EmailVerificationGate />
      </Suspense>
    );
  }

  // Wait for disclosure check to resolve
  if (hasPending === null) {
    return <LoadingScreen />;
  }

  // Gate users with unacknowledged disclosures
  if (hasPending) {
    return <DisclosureGate onComplete={() => setHasPending(false)} />;
  }

  return <>{children}</>;
};

// Redirect authenticated users away from login page
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Issue #15: Update page title on route change
const PAGE_TITLES: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/strategies': 'Strategies',
  '/strategies/create': 'New Thesis',
  '/analytics': 'Analytics',
  '/settings/control-panel': 'Control Panel',
  '/settings': 'Settings',
  '/settings/kalshi': 'Kalshi Connection',
  '/settings/billing': 'Billing',
  '/settings/pricing': 'Pricing',
  '/portfolio/optimize': 'Portfolio Optimizer',
  '/marketplace': 'Marketplace',
  '/marketplace/create': 'List a Strategy',
  '/marketplace/edit': 'Edit Listing',
  '/marketplace/my-listings': 'My Listings',
  '/marketplace/earnings': 'Earnings',
  '/marketplace/purchases': 'My Purchases',
  '/marketplace/help': 'Marketplace Help',
  '/admin': 'Admin',
  '/admin/triage': 'Triage',
  '/admin/usage': 'Usage Analytics',
  '/admin/compliance': 'Compliance Audit',
  '/faq': 'FAQ',
  '/risk-disclosure': 'Risk Disclosure',
  '/methodology': 'How Backtesting Works',
  '/about': 'About Quantato',
  '/terms': 'Terms of Service',
  '/privacy': 'Privacy Policy',
  '/settings/security': 'Security',
  '/settings/tours': 'Guided Tours',
  '/settings/release-notes': "What's New",
  '/login': 'Sign In',
  '/ideas': 'Trading Ideas',
  '/compliance': 'Compliance',
  '/verify-email': 'Verify Email',
};

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    // Known routes get "<Page> — Quantato"; unmapped paths (marketing landing at "/",
    // dynamic detail routes) fall back to the bare brand instead of a misleading "Dashboard".
    const title = PAGE_TITLES[pathname];
    document.title = title ? `${title} — Quantato` : 'Quantato';
  }, [pathname]);
  return null;
}

function AppRoutes() {
  return (
    <Suspense fallback={<LoadingScreen />}>
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route path="/oauth/callback" element={<OAuthCallback />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/terms" element={<Suspense fallback={<LoadingScreen />}><TermsOfService /></Suspense>} />
      <Route path="/privacy" element={<Suspense fallback={<LoadingScreen />}><PrivacyPolicy /></Suspense>} />
      <Route path="/faq" element={<Suspense fallback={<LoadingScreen />}><FAQPage /></Suspense>} />
      <Route path="/about" element={<Suspense fallback={<LoadingScreen />}><AboutPage /></Suspense>} />
      <Route path="/compliance" element={<Suspense fallback={<LoadingScreen />}><CompliancePage /></Suspense>} />
      <Route path="/risk-disclosure" element={<Suspense fallback={<LoadingScreen />}><RiskDisclosure /></Suspense>} />
      <Route
        path="/*"
        element={
          <RequireAuth>
            <TradingModeProvider>
            <ThemedApp>
            <ErrorNotificationProvider>
            <ReadOnlyProvider>
              <StrategyFilterProvider>
                <ManualTradingProvider>
                <OnboardingProvider>
                <TourProvider>
                <SidebarProvider>
                <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
                  <Sidebar />
                  <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden', minWidth: 0 }}>
                    <Box component="header"><Navbar /></Box>
                    <Box component="main" id="main-content" role="main" aria-label="Main content" sx={{ flexGrow: 1, p: { xs: 1.5, sm: 2, md: 3 }, overflow: 'auto', backgroundColor: 'background.default' }}>
                      <RiskDisclosureBanner />
                      <DisclosureBanner />
                      <ReverificationBanner />
                      <TokenWarning />
                      <JurisdictionWarningModal />
                      <CircuitBreakerAlertModal />
                      <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress /></Box>}>
                      <Routes>
                        <Route path="/" element={<Dashboard />} />
                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="/analytics" element={<AnalyticsPage />} />
                        <Route path="/settings/*" element={<SettingsPage />} />
                        {/* Legacy URL redirects */}
                        <Route path="/system" element={<Navigate to="/settings" replace />} />
                        <Route path="/pricing" element={<Navigate to="/settings/pricing" replace />} />
                        <Route path="/security" element={<Navigate to="/settings/security" replace />} />
                        {/* Positions page removed — features relocated to Live Dashboard + Kalshi Connection */}
                        <Route path="/strategies" element={<Strategies />} />
                        <Route path="/strategies/create" element={<StrategyCreate />} />
                        <Route path="/strategies/:id/detail" element={<StrategyDetail />} />
                        <Route path="/strategies/:id/edit" element={<StrategyCreate />} />
                        <Route path="/strategies/:id/backtest" element={<BacktestDashboard />} />
                        <Route path="/ideas" element={<TradingIdeas />} />
<Route path="/research" element={<ResearchFunnel />} />
<Route path="/research/:id" element={<ExperimentDetail />} />
                        <Route path="/portfolio/optimize" element={<PortfolioOptimizer />} />
                        <Route path="/settings/control-panel" element={<AutonomousMode />} />
                        {/* All Trades page removed — sync controls relocated to Kalshi Connection */}
                        <Route path="/marketplace" element={<MarketplaceFeed />} />
                        <Route path="/marketplace/create" element={<MarketplaceCreateListing />} />
                        <Route path="/marketplace/edit/:id" element={<MarketplaceCreateListing />} />
                        <Route path="/marketplace/my-listings" element={<MarketplaceMyListings />} />
                        <Route path="/marketplace/earnings" element={<MarketplaceEarnings />} />
                        <Route path="/marketplace/purchases" element={<MarketplaceSubscriptions />} />
                        <Route path="/marketplace/creator/:id" element={<CreatorProfile />} />
                        <Route path="/marketplace/help" element={<MarketplaceHelp />} />
                        <Route path="/marketplace/:id" element={<MarketplaceListingDetail />} />
                        <Route path="/admin" element={<AdminDashboard />} />
                        <Route path="/admin/triage" element={<AdminTriagePage />} />
                        <Route path="/admin/usage" element={<AdminUsageAnalytics />} />
                        <Route path="/admin/compliance" element={<AdminComplianceDashboard />} />
                        <Route path="/terms" element={<TermsOfService />} />
                        <Route path="/privacy" element={<PrivacyPolicy />} />
                        <Route path="/faq" element={<FAQPage />} />
                        <Route path="/about" element={<AboutPage />} />
                        <Route path="/compliance" element={<CompliancePage />} />
                        <Route path="/risk-disclosure" element={<RiskDisclosure />} />
                        <Route path="/methodology" element={<MonteCarloMethodology />} />
                        {/* Catch-all: any unmatched authed path (retired ideas.* deep links,
                            stale bookmarks) redirects to the dashboard instead of a blank page. */}
                        <Route path="*" element={<Navigate to="/dashboard" replace />} />
                      </Routes>
                      </Suspense>
                    </Box>
                    <Footer />
                  </Box>
                </Box>
                <FeedbackButton />
                <GuidedTour />
                <TourRunner />
                </SidebarProvider>
                </TourProvider>
                </OnboardingProvider>
                </ManualTradingProvider>
              </StrategyFilterProvider>
            </ReadOnlyProvider>
            </ErrorNotificationProvider>
            </ThemedApp>
            </TradingModeProvider>
          </RequireAuth>
        }
      />
    </Routes>
    </Suspense>
  );
}

/** Reads trading mode from context and applies the matching theme */
const ThemedApp: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { tradingMode } = useTradingMode();
  const theme = tradingMode === 'live' ? liveTheme : paperTheme;
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};

function App() {
  return (
    <ThemeProvider theme={paperTheme}>
      <CssBaseline />
      <AuthProvider>
        <ToastProvider>
        <Router>
          <ScrollToTop />
          <UITLAdminWrapper />
          <AppRoutes />
          <PublicFeedbackButton />
        </Router>
        </ToastProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
